<!--
  @component SubNav — the shared sub-header tab bar (Slate Ink).
  tier:   primitive (presentation only — no app/route/data knowledge)

  The shell mounts a `<Feature>Header` in its sub-header slot; that header
  renders THIS to inherit the shared look + behavior. SubNav owns the
  styling, the tab markup, active state, overflow scroll, and click→select.
  The feature supplies:
    • items   — the section tabs (id + label), in order
    • activeId — which tab is current
    • onSelect — click handler
    • lead?    — optional left-side content (title / eyebrow / breadcrumb)
    • actions? — optional right-side content (e.g. a primary action button)
-->
<script lang="ts">
  import type { Snippet } from "svelte";

  interface Tab {
    readonly id: string;
    readonly label: string;
    /** When true, the tab is shown but dimmed + non-interactive (e.g. a
     *  per-project tab on a cross-project landing with no project selected). */
    readonly disabled?: boolean;
  }
  interface Props {
    /** The section tabs to render, in order. */
    items: readonly Tab[];
    /** The active tab id (highlighted). */
    activeId: string | null;
    /** Fired when a tab is clicked, with its id. */
    onSelect: (id: string) => void;
    /** Optional left-side content (title / eyebrow / breadcrumb). */
    lead?: Snippet;
    /** Optional right-side content (actions, e.g. a primary button). */
    actions?: Snippet;
    /** Accessible label for the nav landmark. */
    ariaLabel?: string;
  }
  const {
    items,
    activeId,
    onSelect,
    lead,
    actions,
    ariaLabel = "Sections",
  }: Props = $props();
</script>

<nav class="sub-nav" aria-label={ariaLabel}>
  {#if lead}
    <div class="sub-nav-lead">{@render lead()}</div>
  {/if}

  <div class="sub-nav-tabs">
    {#each items as s (s.id)}
      <button
        type="button"
        aria-current={s.id === activeId ? "page" : undefined}
        class:active={s.id === activeId}
        disabled={s.disabled}
        onclick={() => { if (!s.disabled) onSelect(s.id); }}
      >
        {s.label}
      </button>
    {/each}
  </div>

  {#if actions}
    <div class="sub-nav-actions">{@render actions()}</div>
  {/if}
</nav>

<style>
  .sub-nav {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: 0 var(--space-8);
    border-bottom: 0.5px solid var(--border);
    background: var(--surface-base);
  }
  .sub-nav-lead {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: var(--text-secondary);
    font-size: 13px;
  }
  .sub-nav-tabs {
    display: flex;
    gap: var(--space-1);
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .sub-nav-tabs::-webkit-scrollbar { display: none; }
  .sub-nav-tabs button {
    background: transparent;
    border: 0;
    border-bottom: 2px solid transparent;
    color: var(--text-tertiary);
    padding: 14px var(--space-3) 12px;
    font: inherit;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    transition: color var(--ease-fast), border-color var(--ease-fast);
  }
  .sub-nav-tabs button:hover:not(:disabled) { color: var(--text-secondary); }
  .sub-nav-tabs button.active {
    color: var(--text-primary);
    border-bottom-color: var(--text-primary);
  }
  .sub-nav-tabs button:disabled {
    color: var(--text-ghost);
    cursor: default;
    opacity: 0.55;
  }
  .sub-nav-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-shrink: 0;
    padding-left: var(--space-3);
  }
</style>
